import { Trans } from "@lingui/react/macro";
import { Icon } from "@silversea-ssc/ui-components-react";
import React, { useCallback, useRef } from "react";
import { useOverridePhoneNumberInABTest } from "src/inbound-ab-test-experiment/phone";
import * as styles from "./LightPhoneNumber.module.scss";
import clsx from "clsx";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { isPresent } from "src/utils/checks";
import { useIsDesktop } from "src/utils/useIsTabletOrDesktop";
import { useClickOutside } from "src/utils/useClickOutside";

type Props = {
  className?: string;
};

const LightPhoneNumber: React.FC<Props> = ({ className }) => {
  const geolocation = useGeolocation();
  const phoneNumber = useOverridePhoneNumberInABTest();
  const isDesktop = useIsDesktop();
  const [showTooltip, setShowTooltip] = React.useState(false);
  const { phoneDisclaimer } = geolocation;
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside({
    enable: isPresent(phoneDisclaimer) && showTooltip,
    containerElement: containerRef.current,
    closeFunction: () => {
      setShowTooltip(false);
    }
  });

  const onToggleMobileTooltip = useCallback(
    (e: React.MouseEvent) => {
      if (!isDesktop && phoneDisclaimer) {
        setShowTooltip((currentShowTooltip) => {
          if (!currentShowTooltip) {
            e.preventDefault();
          }
          return !currentShowTooltip;
        });
      }
    },
    [isDesktop, phoneDisclaimer]
  );

  return (
    <div
      ref={containerRef}
      suppressHydrationWarning
      className={clsx(styles.phoneNumberContainer, className, {
        [styles.hoverShowDisclaimer]: isDesktop && isPresent(phoneDisclaimer),
        [styles.manualShowDisclaimer]:
          !isDesktop && isPresent(phoneDisclaimer) && showTooltip
      })}
    >
      <span className={styles.text}>
        <Trans>Let our cruise expert assist you.</Trans>
      </span>
      <Icon
        name="circle-phone"
        padding={false}
        size="1.5x"
        ariaHidden={true}
        wrapperClassName={styles.icon}
      />
      <a
        href={`tel:${phoneNumber}`}
        aria-label={`call ${phoneNumber}`}
        data-content-piece="phone"
        className={styles.phoneNumber}
        onClick={onToggleMobileTooltip}
      >
        {phoneNumber}

        {phoneDisclaimer && (
          <span className={styles.phoneDisclaimer}>{phoneDisclaimer}</span>
        )}
      </a>
    </div>
  );
};

export default LightPhoneNumber;
